import { useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import ExportChartButton from '@/components/Chart/ExportButton/ExportChartButton';
import Loader from '@/components/Loader';
import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import type { FactsheetDetails, FactsheetTab } from '@/features/Factsheet/types';
import useUserDetails from '@/hooks/useUserDetails';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import { AuthenticatedFwdCurveChart, AuthenticatedSpotChart } from './AuthenticatedChart';
import UnauthenticatedChart from './UnauthenticatedChart';

const PREMIUM_MESSAGE = {
  title: 'This is a Premium feature.',
  subtitle: 'Upgrade to GX Go Premium to enable charts and unlock advanced functionality.'
};

const BasicUserMessage = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
    <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>{PREMIUM_MESSAGE.title}</Typography>
    <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>{PREMIUM_MESSAGE.subtitle}</Typography>
  </Box>
);

const BlurBackdrop = () => {
  const { isUserPremium, isUserExpired } = useUserDetails();

  if (isUserPremium && !isUserExpired) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(4px)',
          zIndex: 1400
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          textAlign: 'center',
          transform: 'translateX(-50%)',
          zIndex: 1401,
          width: '100%'
        }}
      >
        <BasicUserMessage />
      </Box>
    </>
  );
};

interface ChartTabContentProps {
  factsheet: FactsheetDetails;
  contentTabs: FactsheetTab[];
  activeTab: number;
}

const ChartTabContent = ({ factsheet, contentTabs, activeTab }: ChartTabContentProps) => (
  <>
    {contentTabs.map((tab, index) => (
      <TabPanel
        key={`${factsheet.code}-chart-${tab.name}`}
        containerSX={{
          flex: 1,
          flexGrow: 1,
          width: '100%',
          overflow: 'hidden',
          ...(index === activeTab ? { display: 'flex' } : {})
        }}
        index={index}
        sx={{
          pt: 2,
          flex: 1,
          flexGrow: 1,
          width: '100%',
          overflow: 'auto'
        }}
        value={activeTab}
      >
        {tab.component}
      </TabPanel>
    ))}
  </>
);

/**
 * Creates a chart series configuration for the factsheet.
 */
function createChartSeries(
  factsheet: FactsheetDetails,
  symbol: { timeRef: string; periodType: string; period?: string },
  spotAlias: string | undefined,
  includePeriod: boolean
): TemplateChartSeries {
  return {
    settings: { type: 'line' },
    shortTitle:
      spotAlias ??
      `${factsheet.code} ${symbol.timeRef} ${symbol.periodType} ${symbol.period ?? ''}`.trim(),
    symbol: {
      group: 'Prod_Indexes',
      key: {
        Code: factsheet.code,
        PeriodType: symbol.periodType,
        TimeRef: symbol.timeRef,
        Period: includePeriod && symbol.period ? symbol.period : ''
      }
    }
  };
}

interface ChartTabProps {
  factsheet: FactsheetDetails;
}

const ChartTab = ({ factsheet }: ChartTabProps) => {
  const spotAlias = factsheet.indexDetails['spotAlias'] as string | undefined;
  const { isUserPremium, isUserExpired, status } = useUserDetails();
  const [activeTab, setActiveTab] = useState(0);

  const isLoading = status === 'loading';
  const isAuthorized = status !== undefined && isUserPremium && !isUserExpired;

  const templateSpotSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.spotSymbols.length === 0) {
      return [];
    }

    const symbol = factsheet.spotSymbols[0];
    return [createChartSeries(factsheet, symbol, spotAlias, true)];
  }, [factsheet, isAuthorized, spotAlias]);

  const templateForwardSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.fwdCurveSymbols.length === 0) {
      return [];
    }

    const symbol = factsheet.fwdCurveSymbols[0];
    return [createChartSeries(factsheet, symbol, spotAlias, false)];
  }, [factsheet, isAuthorized, spotAlias]);

  const contentTabs = useMemo((): FactsheetTab[] => {
    const tabs: FactsheetTab[] = [];

    if (factsheet.spotSymbols.length > 0) {
      tabs.push({
        position: 0,
        name: 'Spot',
        component: <AuthenticatedSpotChart templateChartSeries={templateSpotSeries} />
      });
    }

    if (factsheet.fwdCurveSymbols.length > 0) {
      tabs.push({
        position: 1,
        name: 'Forward',
        component: <AuthenticatedFwdCurveChart templateChartSeries={templateForwardSeries} />
      });
    }

    return tabs;
  }, [
    factsheet.spotSymbols.length,
    factsheet.fwdCurveSymbols.length,
    templateSpotSeries,
    templateForwardSeries
  ]);

  if (isLoading) {
    return <Loader />;
  }

  const hasNoChartData = templateSpotSeries.length === 0 && templateForwardSeries.length === 0;
  const showUnauthenticated = status === undefined || !isUserPremium || isUserExpired;

  return (
    <>
      {/* Tab navigation - only shown for authenticated users with multiple chart types */}
      {isAuthorized && contentTabs.length > 1 && (
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
          <Tabs
            activeTab={activeTab}
            tabs={contentTabs.map(({ name }) => ({ name }))}
            onChange={setActiveTab}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <ExportChartButton
              graphType={templateForwardSeries.length > 0 ? 'forwardSimple' : 'spot'}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
        <BlurBackdrop />

        {/* Unauthenticated placeholder */}
        {showUnauthenticated && <UnauthenticatedChart />}

        {/* No data message */}
        {hasNoChartData && isAuthorized && (
          <Box sx={{ width: '100%', height: 300 }}>
            <Typography>{factsheet.code} does not have any spot items</Typography>
          </Box>
        )}

        {/* Authenticated chart content */}
        {isAuthorized && (
          <ChartTabContent
            activeTab={activeTab}
            contentTabs={contentTabs}
            factsheet={factsheet}
          />
        )}
      </Box>
    </>
  );
};

export default ChartTab;
